import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, X, Upload, Package, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// giraia-loja-v1
export const CATEGORIAS = [
  { value: 'sapatilha', label: '👟 Sapatilha' },
  { value: 'bicicleta', label: '🚲 Bicicleta' },
  { value: 'bike_eletrica', label: '⚡ Bike Elétrica' },
  { value: 'acessorio_eletrica', label: '🔋 Acessório para Bike Elétrica' },
  { value: 'bike_eletrica_seminova', label: '♻️ Bike Elétrica Seminova' },
  { value: 'bike_seminova', label: '♻️ Bike Seminova' },
  { value: 'scooter', label: '🛴 Scooter / Patinete' },
  { value: 'capacete', label: '⛑️ Capacete' },
  { value: 'pecas', label: '🔧 Peças' },
  { value: 'acessorios', label: '🎒 Acessórios' },
  { value: 'vestuario', label: '👕 Vestuário' },
  { value: 'outro', label: '📦 Outro' },
];

const NUMERACAO = ['34', '35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46'];
const ARO_QUADRO = ['Aro 12', 'Aro 16', 'Aro 20', 'Aro 24', 'Aro 26', 'Aro 27.5', 'Aro 29',
  'Quadro 13', 'Quadro 15', 'Quadro 17', 'Quadro 19', 'Quadro 21'];
const ROUPA = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG'];

export const tamanhosDaCategoria = (cat) => {
  if (cat === 'sapatilha') return { titulo: 'Numerações disponíveis', opcoes: NUMERACAO };
  if (['bicicleta', 'bike_eletrica', 'bike_seminova', 'bike_eletrica_seminova'].includes(cat)) return { titulo: 'Aro e quadro disponíveis', opcoes: ARO_QUADRO };
  if (cat === 'vestuario' || cat === 'capacete') return { titulo: 'Tamanhos disponíveis', opcoes: ROUPA };
  return { titulo: 'Tamanhos (se tiver)', opcoes: ['Único'] };
};

const vazio = {
  nome: '', descricao: '', preco: '', preco_promocional: '', categoria: 'sapatilha',
  tamanhos: [], imagens: [], imagem_url: '', estoque: 1, destaque: false, marca: '', is_oficial: false
};

const AdminProductsPage = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(vazio);

  useEffect(() => { fetchProducts(); // eslint-disable-next-line
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(response.data.products);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (product = null) => {
    if (product) {
      const imagens = (product.imagens && product.imagens.length) ? product.imagens : (product.imagem_url ? [product.imagem_url] : []);
      setEditingProduct(product);
      setFormData({
        ...vazio, ...product, imagens,
        preco: product.preco != null ? String(product.preco) : '',
        preco_promocional: product.preco_promocional ? String(product.preco_promocional) : '',
        tamanhos: product.tamanhos || []
      });
    } else {
      setEditingProduct(null);
      setFormData(vazio);
    }
    setShowEditor(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const espaco = 6 - formData.imagens.length;
    if (espaco <= 0) { toast.error('Máximo de 6 fotos por produto'); return; }
    setUploading(true);
    const novas = [];
    for (const file of files.slice(0, espaco)) {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: a foto deve ter menos de 5MB`); continue; }
      const uploadData = new FormData();
      uploadData.append('file', file);
      try {
        const response = await axios.post(`${API_URL}/api/admin/products/upload-image`, uploadData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        novas.push(`${API_URL}${response.data.url}`);
      } catch (error) {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    if (novas.length) {
      setFormData(f => ({ ...f, imagens: [...f.imagens, ...novas] }));
      toast.success(novas.length > 1 ? `${novas.length} fotos enviadas! 📸` : 'Foto enviada! 📸');
    }
    setUploading(false);
  };

  const removerFoto = (i) => setFormData(f => ({ ...f, imagens: f.imagens.filter((_, j) => j !== i) }));
  const fotoPrincipal = (i) => setFormData(f => {
    const imgs = [...f.imagens]; const [x] = imgs.splice(i, 1); return { ...f, imagens: [x, ...imgs] };
  });

  const toggleTamanho = (t) => setFormData(f => ({
    ...f, tamanhos: f.tamanhos.includes(t) ? f.tamanhos.filter(x => x !== t) : [...f.tamanhos, t]
  }));

  const mudarCategoria = (cat) => setFormData(f => ({ ...f, categoria: cat, tamanhos: [] }));

  const preco = parseFloat(String(formData.preco).replace(',', '.'));
  const promo = parseFloat(String(formData.preco_promocional).replace(',', '.'));
  const desconto = preco > 0 && promo > 0 && promo < preco ? Math.round((1 - promo / preco) * 100) : 0;

  const handleSave = async () => {
    if (!formData.nome || !(preco > 0) || formData.imagens.length === 0) {
      toast.error('Preencha nome, preço e pelo menos uma foto');
      return;
    }
    if (formData.preco_promocional && !(promo > 0 && promo < preco)) {
      toast.error('O preço promocional precisa ser menor que o preço normal');
      return;
    }
    setSaving(true);
    const dataToSend = {
      ...formData,
      preco,
      preco_promocional: promo > 0 && promo < preco ? promo : null,
      estoque: parseInt(formData.estoque, 10) >= 0 ? parseInt(formData.estoque, 10) : 0,
      imagem_url: formData.imagens[0],
      is_oficial: false
    };
    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produto atualizado! 🛍️');
      } else {
        await axios.post(`${API_URL}/api/products`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Produto criado! ✨');
      }
      setShowEditor(false);
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este produto da loja?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produto removido');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const nomeCategoria = (v) => (CATEGORIAS.find(c => c.value === v) || {}).label || v;
  const grupo = tamanhosDaCategoria(formData.categoria);
  const input = 'w-full bg-[#050505] border border-[#2D243F] rounded-md px-4 py-3 text-white focus:border-[#E2B84A] outline-none';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E2B84A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="bg-gradient-to-br from-[#120E1A] to-[#1C1628] p-6 border-b border-[#E2B84A]/30">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" className="inline-flex items-center gap-2 text-[#A19BAA] hover:text-white mb-4">
            <ArrowLeft size={20} /><span>Painel Admin</span>
          </Link>
          <div className="flex flex-wrap gap-4 justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Package className="w-8 h-8 text-[#E2B84A]" />
                <h1 className="text-3xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Gerenciar Produtos</h1>
              </div>
              <p className="text-[#A19BAA]">
                {products.length} produtos na loja · <a href="/loja-publica" target="_blank" rel="noreferrer" className="text-[#E2B84A] underline">ver a loja como cliente</a>
              </p>
            </div>
            <button onClick={() => openEditor()} className="bg-[#FFD700] hover:bg-[#F4C430] text-black font-bold rounded-lg px-4 py-2 transition-colors flex items-center gap-2">
              <Plus size={18} /> Novo Produto
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {products.length === 0 ? (
          <div className="text-center py-16 bg-[#120E1A] border border-[#2D243F] rounded-xl">
            <Package className="w-16 h-16 text-[#716A80] mx-auto mb-4" />
            <p className="text-[#A19BAA]">Nenhum produto cadastrado. Clique em "Novo Produto" para começar.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map(product => (
              <div key={product.id} className="bg-[#120E1A] border border-[#2D243F] rounded-xl overflow-hidden hover:border-[#E2B84A]/50 transition-all">
                <div className="aspect-square bg-[#1C1628] overflow-hidden relative">
                  <img src={(product.imagens && product.imagens[0]) || product.imagem_url} alt={product.nome} className="w-full h-full object-cover" />
                  {product.destaque && <span className="absolute top-2 left-2 bg-[#FFD700] text-black text-xs font-black px-2 py-1 rounded-full">⭐ DESTAQUE</span>}
                  {product.estoque <= 0 && <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-black px-2 py-1 rounded-full">ESGOTADO</span>}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{product.nome}</h3>
                  <p className="text-[#716A80] text-xs mb-2">{nomeCategoria(product.categoria)} · estoque: {product.estoque}</p>
                  {product.preco_promocional ? (
                    <p className="mb-3">
                      <span className="text-[#716A80] line-through mr-2">R$ {product.preco.toFixed(2)}</span>
                      <span className="text-2xl font-black text-[#FFD700]">R$ {product.preco_promocional.toFixed(2)}</span>
                    </p>
                  ) : (
                    <p className="text-2xl font-black text-[#FFD700] mb-3">R$ {product.preco.toFixed(2)}</p>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => openEditor(product)} className="flex-1 bg-[#E2B84A]/20 hover:bg-[#E2B84A]/30 text-[#E2B84A] font-bold rounded-lg px-3 py-2 transition-colors flex items-center justify-center gap-2 text-sm">
                      <Edit size={14} /> Editar
                    </button>
                    <button onClick={() => handleDelete(product.id)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold rounded-lg px-3 py-2 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showEditor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#120E1A] border-2 border-[#E2B84A] rounded-2xl p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingProduct ? 'Editar Produto' : 'Novo Produto'}
              </h3>
              <button onClick={() => setShowEditor(false)}><X className="text-[#A19BAA] hover:text-white" /></button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-white font-medium mb-2">Categoria</label>
                <select value={formData.categoria} onChange={(e) => mudarCategoria(e.target.value)} className={input}>
                  {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Fotos do produto * <span className="text-[#716A80] text-sm font-normal">(até 6 · a primeira é a capa)</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {formData.imagens.map((url, i) => (
                    <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-[#2D243F]">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {i === 0 ? (
                        <span className="absolute bottom-1 left-1 bg-[#E2B84A] text-black text-[10px] font-black px-2 py-0.5 rounded">CAPA</span>
                      ) : (
                        <button type="button" onClick={() => fotoPrincipal(i)} className="absolute bottom-1 left-1 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded">usar como capa</button>
                      )}
                      <button type="button" onClick={() => removerFoto(i)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1"><X size={14} className="text-white" /></button>
                    </div>
                  ))}
                  {formData.imagens.length < 6 && (
                    <button type="button" onClick={() => fileInputRef.current.click()} disabled={uploading}
                      className="aspect-square border-2 border-dashed border-[#2D243F] hover:border-[#E2B84A] rounded-lg flex flex-col items-center justify-center text-center p-2">
                      {uploading ? (
                        <div className="w-6 h-6 border-2 border-[#E2B84A] border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-[#E2B84A] mb-1" />
                          <span className="text-white text-xs font-bold">Adicionar fotos</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                <p className="text-[#716A80] text-xs mt-2">Dica: fundo liso, boa luz, uma foto de lado, uma de frente e uma da sola.</p>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-white font-medium mb-2">Nome do produto *</label>
                  <input type="text" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className={input}
                    placeholder={formData.categoria === 'sapatilha' ? 'Ex: Sapatilha Speed Preta com Clip' : 'Ex: Bike Aro 29 Shimano 21 marchas'} />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Marca</label>
                  <input type="text" value={formData.marca || ''} onChange={(e) => setFormData({ ...formData, marca: e.target.value })} className={input} placeholder="Ex: Shimano" />
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Descrição</label>
                <textarea value={formData.descricao} onChange={(e) => setFormData({ ...formData, descricao: e.target.value })} className={input} rows="4"
                  placeholder="Conte para que serve, o material e por que vale a pena. Quem pesquisa no Google lê isso." />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-white font-medium mb-2">Preço normal (R$) *</label>
                  <input type="number" step="0.01" min="0" value={formData.preco} onChange={(e) => setFormData({ ...formData, preco: e.target.value })} className={input} placeholder="0,00" />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Preço promoção (R$)</label>
                  <input type="number" step="0.01" min="0" value={formData.preco_promocional} onChange={(e) => setFormData({ ...formData, preco_promocional: e.target.value })} className={input} placeholder="opcional" />
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Estoque</label>
                  <input type="number" min="0" value={formData.estoque} onChange={(e) => setFormData({ ...formData, estoque: e.target.value })} className={input} />
                </div>
              </div>
              {desconto > 0 && (
                <p className="text-[#10B981] text-sm font-bold -mt-2">Na loja vai aparecer: <span className="line-through text-[#716A80]">R$ {preco.toFixed(2)}</span> → R$ {promo.toFixed(2)} com selo de {desconto}% OFF</p>
              )}

              <div>
                <label className="block text-white font-medium mb-2">{grupo.titulo}</label>
                <div className="flex flex-wrap gap-2">
                  {grupo.opcoes.map(t => (
                    <button key={t} type="button" onClick={() => toggleTamanho(t)}
                      className={`px-3 py-2 rounded-lg font-bold transition-colors ${formData.tamanhos.includes(t)
                        ? 'bg-[#E2B84A] text-black' : 'bg-[#050505] border border-[#2D243F] text-[#A19BAA] hover:border-[#E2B84A]'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer bg-[#050505] border border-[#FFD700]/30 rounded-lg p-4">
                <input type="checkbox" checked={!!formData.destaque} onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })} className="mt-1 w-5 h-5 accent-[#FFD700]" />
                <div>
                  <p className="text-[#FFD700] font-bold flex items-center gap-2"><Star size={16} /> Destacar na vitrine</p>
                  <p className="text-[#A19BAA] text-xs mt-1">O produto aparece primeiro na loja, com selo de destaque. Use para promoções e lançamentos.</p>
                </div>
              </label>

              <div className="flex gap-3">
                <button onClick={() => setShowEditor(false)} className="flex-1 bg-transparent border border-[#2D243F] text-[#A19BAA] font-bold rounded-lg px-4 py-3 hover:bg-[#2D243F] transition-colors">Cancelar</button>
                <button onClick={handleSave} disabled={saving || uploading} className="flex-1 bg-[#E2B84A] hover:bg-[#F0D98C] text-black font-bold rounded-lg px-4 py-3 transition-colors disabled:opacity-50">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
